/**
 * Shared `force*`-state class helpers for the sim-mode ("recorded user
 * session") widgets.
 *
 * CSS pseudo-states (`:hover`, `:focus-visible`, `:active`) can't be driven
 * from JS, so a scripted sim that paints a simulated cursor over a form needs
 * a way to make each control show its hover / focus / pressed VISUAL with no
 * real pointer. Every interactive primitive that opts in exposes optional
 * `forceHover` / `forceFocus` / `forcePressed` props (all `?: boolean`,
 * default `false` → production and other sims unaffected) and routes them
 * through one of these helpers.
 *
 * IMPORTANT: the returned strings MUST be LITERAL class strings. Tailwind's
 * v4 JIT scanner only generates the unprefixed utility if it appears verbatim
 * in source; a runtime-derived class (e.g. slicing `hover:` off a cva string)
 * is never compiled, so it's a silent no-op. `cn` / tailwind-merge then lets
 * the forced token override the base one because it trails the cascade.
 *
 * Button owns its own `forcedColorClasses` (its state visual is a bespoke
 * cva color curve, plus the lifted-3D transform branch); these helpers cover
 * the border-driven field primitives (Input, Select trigger, NumberField)
 * whose hover/focus visual is just a border-color swap. Keeping them here (vs.
 * duplicating the literal strings per file) means the border tokens live in
 * exactly one place.
 */

/**
 * Border-color classes for a field whose hover = `border-charcoal-700` and
 * focus = `border-charcoal-500` (the shared Input / Select-trigger recipe).
 * Focus wins over hover (more specific state). Returns `""` when neither is
 * forced, so callers can drop it into a `cn(...)` unconditionally.
 */
export function forcedFieldBorderClasses(hover: boolean, focus: boolean): string {
    if (focus) return "border-charcoal-500";
    if (hover) return "border-charcoal-700";
    return "";
}
